import { AxiosRestService } from './AxiosRestService';
import { IRestService } from './IRestService';

export interface InventorySummaryDto {
    summaryDate: string;
    partsPackaged: number;
    partsUnpackaged: number;
    partsTotal: number;
    packagedValue: number;
    unpackagedValue: number;
    totalValue: number;
    assembliesPackaged: number;
    assembliesUnpackaged: number;
    assembliesTotal: number;
    assembliesRawValue: number;
    assembliesMarketValue: number;
    subAssembliesPackaged: number;
    subAssembliesUnpackaged: number;
    subAssembliesTotal: number;
    subAssembliesRawValue: number;
    subAssembliesMarketValue: number;
    totalPackaged: number;
    totalUnpackaged: number;
    totalInventory: number;
    totalRawValue: number;
    totalMarketValue: number;
}

export interface InventoryTimeSeriesDto {
    dataPoints: InventorySummaryDto[];
    startDate: string;
    endDate: string;
    interval: string;
}

export interface AverageWeeksInventoryDto {
    calculationDate: string;
    averageWeeksOnHand: number;
    totalEffectiveInventory: number;
    totalAverageSalesPerWeek: number;
    partsIncluded: number;
    includedPartIds: string[];
}

export interface AverageWeeksInventoryTimeSeriesDto {
    dataPoints: AverageWeeksInventoryDto[];
    startDate: string;
    endDate: string;
    interval: string;
    partIds: string[];
}

export interface PartInventorySummaryDto {
    partId: string;
    partNumber: string;
    description: string;
    totalQuantity: number;
    packagedQuantity: number;
    unpackagedQuantity: number;
    unitCost: number;
}

export interface IAnalyticsService extends IRestService {
    getCurrentInventorySummary(): Promise<InventorySummaryDto>;
    getInventorySummary(date: string): Promise<InventorySummaryDto>;
    getInventoryTimeSeries(startDate: string, endDate: string, interval: string): Promise<InventoryTimeSeriesDto>;
    exportInventorySummary(date: string): Promise<void>;
    getAverageWeeksInventory(date: string, partIds: string[]): Promise<AverageWeeksInventoryDto>;
    getAverageWeeksInventoryTimeSeries(startDate: string, endDate: string, interval: string, partIds: string[]): Promise<AverageWeeksInventoryTimeSeriesDto>;
    getPartsInventorySummary(date: string): Promise<PartInventorySummaryDto[]>;
    getCurrentPartsInventorySummary(): Promise<PartInventorySummaryDto[]>;
}

export class AnalyticsService extends AxiosRestService implements IAnalyticsService {
    constructor() {
        super();
    }

    async getCurrentInventorySummary(): Promise<InventorySummaryDto> {
        try {
            const response = await this.get<InventorySummaryDto>('analytics/inventory-summary/current');
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve current inventory summary');
        }
    }

    async getInventorySummary(date: string): Promise<InventorySummaryDto> {
        try {
            const response = await this.get<InventorySummaryDto>(`analytics/inventory-summary/${date}`);
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve inventory summary');
        }
    }

    async getInventoryTimeSeries(startDate: string, endDate: string, interval: string): Promise<InventoryTimeSeriesDto> {
        try {
            const params = new URLSearchParams({ startDate, endDate, interval });
            const response = await this.get<InventoryTimeSeriesDto>(`analytics/inventory-timeseries?${params}`);
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve time series data');
        }
    }

    async exportInventorySummary(date: string): Promise<void> {
        try {
            const params = new URLSearchParams({ date });
            const response = await this.get<Blob>(`analytics/inventory-export?${params}`);
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `inventory-analytics-${date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            throw new Error('Failed to export inventory summary');
        }
    }

    async getAverageWeeksInventory(date: string, partIds: string[]): Promise<AverageWeeksInventoryDto> {
        try {
            const requestBody = {
                date: date,
                partIds: partIds
            };
            const response = await this.post<typeof requestBody, AverageWeeksInventoryDto>('analytics/average-weeks-inventory', requestBody);
            return response;
        } catch (error) {
            throw new Error('Failed to calculate average weeks inventory');
        }
    }

    async getAverageWeeksInventoryTimeSeries(startDate: string, endDate: string, interval: string, partIds: string[]): Promise<AverageWeeksInventoryTimeSeriesDto> {
        try {
            const requestBody = {
                startDate,
                endDate,
                interval,
                partIds
            };
            const response = await this.post<typeof requestBody, AverageWeeksInventoryTimeSeriesDto>('analytics/average-weeks-inventory-timeseries', requestBody);
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve average weeks inventory time series');
        }
    }

    async getPartsInventorySummary(date: string): Promise<PartInventorySummaryDto[]> {
        try {
            const response = await this.get<PartInventorySummaryDto[]>(`analytics/parts-inventory-summary/${date}`);
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve parts inventory summary');
        }
    }

    async getCurrentPartsInventorySummary(): Promise<PartInventorySummaryDto[]> {
        try {
            const response = await this.get<PartInventorySummaryDto[]>('analytics/parts-inventory-summary/current');
            return response;
        } catch (error) {
            throw new Error('Failed to retrieve current parts inventory summary');
        }
    }
}
